package one.devsbox.www.ui.company.edit

import androidx.compose.animation.Crossfade
import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import one.devsbox.shared.devices.ApplicationType
import one.devsbox.shared.devices.WWW_APPS
import one.devsbox.www.tools.ArgsId
import one.devsbox.www.ui.widgets.*
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState

@Composable
@ComposableNavNode(ArgsId::class)
fun CompanyEUI() {
    with(CompanyECtx.get()) {
        ContentCompanyEUI()
    }
}

@Composable
context(ctx: CompanyECtx)
private fun ContentCompanyEUI() {
    Box(modifier = Modifier.fillMaxSize().background(MaterialTheme.colorScheme.background)) {
        Crossfade(targetState = ctx.vm.state) { state ->
            when (state) {
                is DataState.Idle -> Unit
                is DataState.Loading -> FullscreenLoadingUI()
                is DataState.Error -> {
                    FullscreenErrorUI(
                        message = state.message,
                        onBack = { ctx.action.back() },
                        onReload = { ctx.action.reload() }
                    )
                }

                is DataState.Result -> Box(modifier = Modifier.fillMaxSize()) {
                    SuccessUI()
                    ContentActions(
                        contentActions = ctx.vm.contentActions,
                        colorInactive = ctx.colorSecondaryContainer,
                        colorActive = ctx.colorPrimaryContainer
                    )
                }
            }
        }

        ctx.vm.warningMessage?.let { msg ->
            AlertDialog(
                onDismissRequest = { ctx.action.clearWarning() },
                title = { Text(stringResource(Res.string.ui_devices_edit_deviceeui_error)) },
                text = { Text(msg) },
                confirmButton = {
                    TextButton(onClick = { ctx.action.clearWarning() }) {
                        Text(stringResource(Res.string.ui_devices_edit_deviceeui_ok))
                    }
                }
            )
        }
    }
}

@Composable
context(ctx: CompanyECtx)
private fun SuccessUI() {
    val isNew = ctx.args.id <= 0

    Column(modifier = Modifier.fillMaxSize().padding(16.dp)) {
        Text(
            text = if (isNew) "NEW COMPANY" else "COMPANY EDITOR",
            style = MaterialTheme.typography.headlineMedium,
            fontWeight = FontWeight.Bold
        )

        Spacer(Modifier.height(16.dp))

        BoxWithConstraints(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
        ) {
            val isWide = maxWidth >= 800.dp
            if (isWide) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(32.dp)
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        CompanyNameSection()
                    }
                    Column(modifier = Modifier.weight(1f)) {
                        CompanyAppTypesSection()
                    }
                }
            } else {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(24.dp)
                ) {
                    CompanyNameSection()
                    CompanyAppTypesSection()
                }
            }
        }
    }
}

@Composable
context(ctx: CompanyECtx)
private fun CompanyNameSection() {
    LabelSection("COMPANY NAME")
    Spacer(Modifier.height(8.dp))
    OutlinedTextField(
        value = ctx.vm.name,
        onValueChange = { ctx.vm.name = it },
        modifier = Modifier.fillMaxWidth(),
        textStyle = MaterialTheme.typography.headlineSmall.copy(
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.onSurface
        ),
        colors = OutlinedTextFieldDefaults.colors(
            focusedContainerColor = Color.Transparent,
            unfocusedContainerColor = Color.Transparent,
            focusedBorderColor = MaterialTheme.colorScheme.outlineVariant,
            unfocusedBorderColor = MaterialTheme.colorScheme.outlineVariant
        ),
        shape = RoundedCornerShape(0.dp)
    )
}

@Composable
context(ctx: CompanyECtx)
private fun CompanyAppTypesSection() {
    LabelSection("APPLICATION TYPES")
    Spacer(Modifier.height(8.dp))
    ApplicationType.WWW_APPS.forEach { appType ->
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp)
        ) {
            Checkbox(
                checked = appType match ctx.vm.appTypeMask,
                onCheckedChange = { checked ->
                    ctx.action.toggleAppType(appType, checked)
                }
            )
            Spacer(Modifier.width(8.dp))
            Text(
                text = stringResource(appType.label),
                style = MaterialTheme.typography.bodyLarge
            )
        }
    }
}
